import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";
import { Preferences } from "@capacitor/preferences";
import { report } from "@services/Sentry.ts";

/**
 * Secure storage for sensitive data like auth tokens
 * - Native (iOS): Uses Keychain via SecureStoragePlugin
 * - Native (Android): Uses Keystore via SecureStoragePlugin
 * - Web/PWA: Uses localStorage with base64 encoding via SecureStoragePlugin
 */
export const secureStorage = {
  async set(key: string, value: string): Promise<void> {
    try {
      await SecureStoragePlugin.set({ key, value });
    } catch (error) {
      console.error("Error setting secure storage:", error);
      throw error;
    }
  },

  async get(key: string): Promise<string | null> {
    try {
      const { value } = await SecureStoragePlugin.get({ key });
      return value || null;
    } catch {
      // Key doesn't exist or other error
      return null;
    }
  },

  async remove(key: string): Promise<void> {
    try {
      await SecureStoragePlugin.remove({ key });
    } catch (error) {
      console.error("Error removing from secure storage:", error);
      report(error);
    }
  },

  async clear(): Promise<void> {
    try {
      await SecureStoragePlugin.clear();
    } catch (error) {
      console.error("Error clearing secure storage:", error);
      report(error);
    }
  },
};

/**
 * Regular storage for non-sensitive data like preferences
 * Uses UserDefaults (iOS) and SharedPreferences (Android)
 */
export const preferences = {
  async set(key: string, value: string): Promise<void> {
    await Preferences.set({ key, value });
  },

  async get(key: string): Promise<string | null> {
    const { value } = await Preferences.get({ key });
    return value;
  },

  async remove(key: string): Promise<void> {
    await Preferences.remove({ key });
  },

  async clear(): Promise<void> {
    await Preferences.clear();
  },
};

// Storage keys constants
export const StorageKeys = {
  // Secure storage keys (sensitive data)
  AUTH_TOKEN: "auth_token",
  REFRESH_TOKEN: "refresh_token",
  API_KEY: "api_key",

  // Preferences keys (non-sensitive data)
  THEME: "theme",
  LANGUAGE: "language",
  LAST_SYNC: "last_sync",
  USER_ID: "user_id",
  USER_NAME: "user_name",
  USER_ROLE: "user_role",
  DARK_MODE: "dark_mode",
  CURRENT_ORGANISATION: "current_organisation",
  REGISTRATION_EMAIL: "registration_email",
} as const;

// Helper functions for common operations
export const auth = {
  async saveToken(token: string): Promise<void> {
    await secureStorage.set(StorageKeys.AUTH_TOKEN, token);
  },

  async getToken(): Promise<string | null> {
    return await secureStorage.get(StorageKeys.AUTH_TOKEN);
  },

  async removeToken(): Promise<void> {
    await secureStorage.remove(StorageKeys.AUTH_TOKEN);
  },

  async clearAuth(): Promise<void> {
    await secureStorage.remove(StorageKeys.AUTH_TOKEN);
    await secureStorage.remove(StorageKeys.REFRESH_TOKEN);
    await preferences.remove(StorageKeys.USER_ID);
    await preferences.remove(StorageKeys.USER_NAME);
    await preferences.remove(StorageKeys.USER_ROLE);
    await preferences.remove(StorageKeys.CURRENT_ORGANISATION);
    await preferences.remove(StorageKeys.REGISTRATION_EMAIL);
  },
};

export const userPrefs = {
  async setTheme(theme: "light" | "dark" | "auto"): Promise<void> {
    await preferences.set(StorageKeys.THEME, theme);
  },

  async getTheme(): Promise<"light" | "dark" | "auto"> {
    const theme = await preferences.get(StorageKeys.THEME);
    return (theme as "light" | "dark" | "auto") || "auto";
  },

  async setLanguage(language: string): Promise<void> {
    await preferences.set(StorageKeys.LANGUAGE, language);
  },

  async getLanguage(): Promise<string> {
    const language = await preferences.get(StorageKeys.LANGUAGE);
    return language || "nl";
  },
};

// Organisation type for storage (matches API response)
export interface StoredOrganisation {
  id: string;
  name: string;
  theme?: string;
  role: "admin" | "employee";
}

export const organisationStorage = {
  async setCurrentOrganisation(org: StoredOrganisation): Promise<void> {
    await preferences.set(
      StorageKeys.CURRENT_ORGANISATION,
      JSON.stringify(org)
    );
  },

  async getCurrentOrganisation(): Promise<StoredOrganisation | null> {
    const value = await preferences.get(StorageKeys.CURRENT_ORGANISATION);
    if (!value) return null;
    try {
      return JSON.parse(value) as StoredOrganisation;
    } catch {
      return null;
    }
  },

  async clearCurrentOrganisation(): Promise<void> {
    await preferences.remove(StorageKeys.CURRENT_ORGANISATION);
  },
};

export const registrationStorage = {
  async setEmail(email: string): Promise<void> {
    await preferences.set(StorageKeys.REGISTRATION_EMAIL, email);
  },

  async getEmail(): Promise<string | null> {
    return await preferences.get(StorageKeys.REGISTRATION_EMAIL);
  },

  async clearEmail(): Promise<void> {
    await preferences.remove(StorageKeys.REGISTRATION_EMAIL);
  },
};
